import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import styled, { css } from 'styled-components';

import { HtmlContentLoader, WysiwygStyleWrapper } from '@powdr/components';
import { Link } from '@powdr/components/link';
import {
  AlternatingBlockLayoutOptions, BlockTypes, Breakpoints, ThemeProps as Prop,
} from '@powdr/constants';
import { useWindowResize } from '@powdr/hooks';
import { ContentBlockType } from '@powdr/model';
import { themeUtil, getBackgroundImageSrc, clamp } from '@powdr/utils';

const CPNT = BlockTypes.SPLIT_3;

// styled components
const ContentBlockSplit3 = styled(Link)`
  display: flex;
  flex-direction: ${({ alternateBlockLayoutState }) => (
    (alternateBlockLayoutState === AlternatingBlockLayoutOptions.DEFAULT)
      ? 'row'
      : 'row-reverse'
  )};
  justify-content: center;
  position: relative;
  width: 100%;
  min-height: 530px;
  overflow: hidden;

  &:focus .button {
    color: ${({ colorProfile }) => themeUtil(Prop.BTN_TXT_HOVER, CPNT, colorProfile)};
    background-color: ${({ colorProfile }) => themeUtil(Prop.BTN_BG_HOVER, CPNT, colorProfile)};
    outline: 2px solid ${({ colorProfile }) => themeUtil(Prop.BTN_BG_HOVER, CPNT, colorProfile)};
    outline-offset: 2px;
  }

  ${Breakpoints.mobile(css`
    flex-direction: column-reverse;
  `)}

  .cb-title {
    margin-bottom: 15px;
  }

  .cb-copy {
    margin-bottom: 15px;
  }

  .cb-col {
    width: 50%;

    ${Breakpoints.mobile(css`
      width: 100%;
    `)}
  }

  .cb-image {
    display: block;
    width: 50%;

    ${Breakpoints.mobile(css`
      width: 100%;
      height: 290px;
    `)}
  }

  .cb-content {
    background-color: ${({ colorProfile }) => themeUtil(Prop.BACKGROUND, CPNT, colorProfile)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;

    ${Breakpoints.mobile(css`
      width: 100%;
      height: 50%;
    `)}

    .button {
      display: inline-block;
    }

    .row {
      text-align: left;
      width: 100%;
      padding: 60px;
      background: transparent;
      position: relative;
      z-index: 1;

      ${Breakpoints.mobile(css`
        padding: 15px;
      `)}

      &.details {
        min-height: 335px;
        overflow: visible;

        ${Breakpoints.mobile(css`
          min-height: auto;
          height: auto;
        `)}
      }
    }
  }
`;

const BGImage = styled.div`
  display: block;
  width: 50%;
  background-image: ${({ image }) => `url('${image}')`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

export const Split3 = ({
  title, copy, formattedContent, blockType, colorProfile, ariaLabel,
  buttonEnabled, buttonLink, buttonText, buttonTarget, buttonClick,
  image, columnOption, isStaticImageLink, alternateBlockLayoutState,
  inert,
}) => {
  const [bgImage, setBgImage] = useState('');
  const resize = useWindowResize();

  useEffect(() => {
    if (resize.windowWidth) {
      setBgImage(
        (!isStaticImageLink)
          ? getBackgroundImageSrc(image?.src, resize.windowWidth, columnOption)
          : image.src,
      );
    }
  }, [resize.windowWidth]);

  return (
    <ContentBlockSplit3
      href={buttonLink}
      target={buttonTarget}
      colorProfile={colorProfile}
      onClick={() => buttonClick()}
      aria-label={ariaLabel}
      role="button"
      className={`content-block-${blockType} `}
      alternateBlockLayoutState={alternateBlockLayoutState}
      inert={inert}
    >
      <div className="cb-content cb-col">
        <div className="row details">
          {(title) && <h3 className="cb-title">{title}</h3>}
          {(copy) && <p className="cb-copy">{copy}</p>}
          {(formattedContent) && <WysiwygStyleWrapper className="cb-formatted-content"><HtmlContentLoader html={formattedContent} allowRerender /></WysiwygStyleWrapper>}
          {(buttonEnabled && buttonText) && (
            <div className="button with-background ">
              {buttonText}
            </div>
          )}
        </div>
      </div>
      <BGImage className="cb-image cb-col" image={bgImage} aria-label={image?.altText} />
    </ContentBlockSplit3>
  );
};

Split3.propTypes = PropTypes.objectOf(ContentBlockType).isRequired;
